import { useEffect, useState } from "react";
import CheckBox from "../../console/components/common/checkBox";
import { ReactComponent as Dropdown } from "../../console/assets/icons/dropdown.svg";
import { ReactComponent as PrintOn } from "../../console/assets/icons/printOn.svg";
import { ReactComponent as PrintOff } from "../../console/assets/icons/printOff.svg";
import { ReactComponent as Empty } from "../../common/assets/icons/empty.svg";
import { api_uri } from "../../common/hooks/utils/enum";
import { OrderData } from "../../common/hooks/console/transaction/useGetOrder";

// 배송시간 뒤에 붙는 표기. 서버가 "13시 20분 예식" 을 "13:20" 으로 줄이면서 떨어져 나간다
const DELIVERY_LABELS = ["예식", "행사", "도착", "까지"];

const deliveryLabel = (deliveryDate?: string, deliveryTime?: string | null) => {
    if (!deliveryTime) return "";
    const label = DELIVERY_LABELS.find(item => deliveryTime.includes(item));
    if (!label || deliveryDate?.includes(label)) return "";
    return label;
};

/**
 * 지점 포털 주문 테이블
 *
 * 본점 TransactionTable 과 동일한 레이아웃. 다른 점은 지점에 노출하면 안 되는 것들뿐이다.
 *  - 판매금액 / 입금상태 / 입금등록 : 컬럼 자체를 두지 않는다 (응답에서도 마스킹돼 내려온다)
 *  - 인수증 출력 / 청구일자        : 본점 업무
 */
type BranchTableProps = {
    data?: OrderData[];
    /* 상세 패널이 열려 있는 주문. 그 행을 핑크로 표시한다 */
    activeTabId?: number | null;
    selectedRow: (order_idx: number) => void;
    selectedRows: (selectedIds: number[]) => void;
    clearTrigger: boolean;
    onClearComplete: () => void;
    field: (field: string) => void;
};

const BranchTable = ({
    data = [],
    activeTabId,
    selectedRow,
    selectedRows,
    clearTrigger,
    onClearComplete,
    field,
}: BranchTableProps) => {
    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
    /* 준비완료 사진 크게 보기 */
    const [readyPop, setReadyPop] = useState<{ images: string[]; sender: string; word: string } | null>(null);
    /* 어떤 열을 어느 방향으로 정렬 중인지. 열이 바뀌면 오름차순부터 시작한다 */
    const [sort, setSort] = useState<{ field: string; order: "ASC" | "DESC" }>({ field: "", order: "DESC" });
    const isAllSelected = data.length > 0 && selectedOrders.length === data.length;

    const handleSort = (fieldName: string) => {
        /*
        // 다음 방향을 먼저 계산해서 그 값을 넘긴다.
        // setState 직후의 상태값은 아직 이전 값이라, 그걸 넘기면 한 박자씩 밀린다.
        */
        const order = sort.field === fieldName && sort.order === "ASC" ? "DESC" : "ASC";
        setSort({ field: fieldName, order });
        field(`${fieldName},${order}`);
    };

    const handleRowSelect = (order_idx: number) => {
        setSelectedOrders(prevSelected => {
            const updated = prevSelected.includes(order_idx)
                ? prevSelected.filter(id => id !== order_idx)
                : [...prevSelected, order_idx];
            selectedRows(updated);
            return updated;
        });
    };

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedOrders([]);
            selectedRows([]);
        } else {
            const allIds = data.map(order => order.order_idx);
            setSelectedOrders(allIds);
            selectedRows(allIds);
        }
    };

    useEffect(() => {
        if (clearTrigger) {
            setSelectedOrders([]);
            selectedRows([]);
            onClearComplete();
        }
    }, [clearTrigger, selectedRows, onClearComplete]);

    return (
        <div className="h-full overflow-auto w-full">
            <table className="table-auto w-full text-sm min-w-[100px]">
                {/* Header */}
                <thead className="bg-primary3 border-b border-gray3 shadow-md h-[60px]">
                    <tr>
                        <th className="border-b border-b-gray3 border-r border-r-gray4 px-3 w-[40px]">
                            <CheckBox checked={isAllSelected} onChange={handleSelectAll} />
                        </th>
                        <th className="border-b min-w-[210px] border-b-gray3 border-r border-r-gray4 px-3 text-center">
                            <div className="flex flex-col gap-1 items-center">
                                <div className="flex items-center gap-1 ml-1">
                                    {/* 배송일자는 날짜만, 배송시간은 시간까지 포함해 정렬한다 */}
                                    <p className="text-base">배송일자</p>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("delivery_day")} />
                                    <p className="text-gray2 font-normal ml-1">배송시간</p>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("delivery_date")} />
                                </div>
                                <p className="text-gray2 font-normal">주문상태</p>
                            </div>
                        </th>
                        <th className="border-b border-b-gray3 font-normal border-r border-r-gray4 w-full">
                            <div className="flex h-[60px] w-full">
                                {/* 주문자 칸을 걷어낸 자리에 명의를 2줄 병합으로 세운다 */}
                                <div className="w-[250px] shrink-0 h-full flex items-center gap-1 px-2 border-r border-gray4 justify-between">
                                    <span>명의</span>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("wreath_sender")} />
                                </div>
                                <div className="flex flex-col w-full">
                                    <div className="flex h-[30px] px-2 min-w-[200px] w-full justify-between items-center">
                                        <span className="whitespace-nowrap">상품</span>
                                        <Dropdown className="cursor-pointer" onClick={() => handleSort("product_name")} />
                                    </div>
                                    <p className="h-[30px] flex items-center justify-start px-2 border-t border-gray4">배송주소</p>
                                </div>
                            </div>
                        </th>
                        <th className="md:min-w-[540px] min-w-[540px]">
                            <div className="flex h-[30px] font-normal">
                                <div className="px-2 h-full flex items-center justify-start w-[160px] shrink-0 border-r">발주금액</div>
                                <div className="px-2 h-full flex items-center justify-start w-[380px] shrink-0 border-r">특이사항</div>
                            </div>
                            <div className="flex h-[30px] font-normal items-center border-t border-gray4">
                                <div className="w-full h-full flex items-center px-2 justify-start">받으시는 분</div>
                            </div>
                        </th>
                    </tr>
                </thead>

                {/* Body */}
                <tbody>
                    {data.length > 0 ? (
                        data.map(order => (
                            <tr
                                key={order.order_idx}
                                className={`cursor-pointer group hover:bg-[#E6EEDD] ${
                                    activeTabId === order.order_idx ? "bg-[#F9EBEB]" : ""
                                }`}
                                onClick={() => selectedRow(order.order_idx)}
                            >
                                <td className="border-b border-gray4" onClick={e => e.stopPropagation()}>
                                    <CheckBox
                                        className="flex justify-center"
                                        checked={selectedOrders.includes(order.order_idx)}
                                        onChange={() => handleRowSelect(order.order_idx)}
                                    />
                                </td>
                                <td className="border border-gray4 px-3 text-center py-2 vertical-top">
                                    <div className="flex flex-col text-gray2 gap-1 items-center">
                                        <div className="flex gap-1 items-center">
                                            <p className="text-[15px] font-bold whitespace-nowrap text-gray1">{order.order_number}</p>
                                            <p className="whitespace-nowrap">
                                                {order.delivery_date?.replace(/\s*\/\s*/g, "/")}
                                                {deliveryLabel(order.delivery_date, order.delivery_time) && (
                                                    <span className="ml-1">
                                                        {deliveryLabel(order.delivery_date, order.delivery_time)}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div
                                                className={`py-[3px] text-[13px] whitespace-nowrap rounded-3xl px-1.5 font-bold ${
                                                    order.order_status === 1
                                                        ? "bg-[#FFC700] text-white px-[14px]"
                                                        : order.order_status === 2
                                                          ? "border border-success text-success"
                                                          : order.order_status === 3
                                                            ? "border border-warning text-warning"
                                                            : order.order_status === 4
                                                              ? "border border-skyblue text-skyblue"
                                                              : order.order_status === 5
                                                                ? "border border-grayicon text-grayicon"
                                                                : order.order_status === 6
                                                                  ? "bg-gray4 px-5 text-gray3"
                                                                  : "bg-error px-5 text-white"
                                                }`}
                                            >
                                                {order.order_status === 1
                                                    ? "미확인"
                                                    : order.order_status === 2
                                                      ? "접수완료"
                                                      : order.order_status === 3
                                                        ? "준비완료"
                                                        : order.order_status === 4
                                                          ? "배송완료"
                                                          : order.order_status === 5
                                                            ? "주문취소"
                                                            : order.order_status === 6
                                                              ? "보류"
                                                              : "삭제"}
                                            </div>
                                            {/* 지점에서 인수증을 뽑았는지. 본점 is_printed 와 별개다 */}
                                            {order.branch_printed ? <PrintOn /> : <PrintOff />}
                                            {/* 준비완료로 올린 사진. 본점 목록과 달리 점멸은 없다.
                                                올린 당사자라 확인 여부를 표시할 이유가 없다 */}
                                            {!!order.ready_images?.length && (
                                                <img
                                                    alt="준비완료"
                                                    src={`${api_uri}/${order.ready_images[0]}`}
                                                    className="w-[26px] h-[26px] shrink-0 object-cover rounded-md border border-gray4 cursor-pointer"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setReadyPop({
                                                            images: order.ready_images || [],
                                                            sender: order.wreath_sender || "",
                                                            word: order.wreath_word || "",
                                                        });
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="border border-gray4 text-left max-w-[650px]">
                                    <div className="flex h-full text-gray2">
                                        <p className="w-[250px] shrink-0 border-r border-gray4 flex items-center p-2 justify-start">
                                            {order.wreath_sender}
                                        </p>
                                        <div className="flex flex-col w-full">
                                            <div className="flex p-2 min-h-12 items-center flex-wrap border-b border-gray4">
                                                <span>{order.product_name}</span>
                                            </div>
                                            <p className="p-2 min-h-12 flex items-center">
                                                {order.delivery_address}
                                                {order.delivery_address_detail ? " " + order.delivery_address_detail : ""}
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td className="border-b align-top">
                                    <div className="h-full flex flex-col">
                                        <div className="flex">
                                            <div
                                                className={`border-r truncate px-2 border-gray4 justify-end items-center flex w-[160px] shrink-0 group-hover:bg-[#E6EEDD] ${
                                                    activeTabId === order.order_idx ? "bg-[#F9EBEB]" : "bg-primary4"
                                                }`}
                                            >
                                                {order.order_amount?.toLocaleString()}원
                                            </div>
                                            <div className="px-2 py-1 flex flex-col gap-0.5 justify-center w-[380px] shrink-0 min-h-12 border-r border-gray4 break-words">
                                                {order.branch_del === "Y" && <span className="text-error font-bold">삭제됨</span>}
                                                {order.branch_req && <span className="text-blue">{order.branch_req}</span>}
                                                {order.customer_request && (
                                                    <span className="text-blue">{order.customer_request}</span>
                                                )}
                                                {order.branch_del !== "Y" && !order.branch_req && !order.customer_request && (
                                                    <span className="text-gray3">-</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex w-full h-full border-t border-gray4">
                                            <div className="px-2 min-h-12 flex items-center">
                                                {order.recipient_name}{" "}
                                                {order.event_content?.depth2
                                                    ? order.event_content.depth2
                                                    : order.event_content?.depth1 === "직접입력"
                                                      ? order.event_content.event_content
                                                      : ""}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="text-center">
                                <div className="flex flex-col items-center gap-5 py-4 mt-52">
                                    <Empty />
                                    <p className="text-grayicon text-lg">등록된 내역이 없습니다.</p>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* 준비완료 사진 크게 보기 */}
            {readyPop && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200] p-6"
                    onClick={() => setReadyPop(null)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-custom max-w-[90vw] max-h-[90vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray4">
                            <p className="text-xl font-bold">준비완료 사진</p>
                            <button className="text-gray3 text-2xl leading-none" onClick={() => setReadyPop(null)}>
                                ×
                            </button>
                        </div>
                        <div className="flex flex-col gap-2 px-6 py-4 border-b border-gray4 text-sm">
                            <p>
                                <span className="text-gray3">명의</span>{" "}
                                <span className="font-bold whitespace-pre-wrap">{readyPop.sender || "-"}</span>
                            </p>
                            <p>
                                <span className="text-gray3">경조사어</span>{" "}
                                <span className="font-bold">{readyPop.word || "-"}</span>
                            </p>
                        </div>

                        <div className="flex gap-3 p-6 overflow-auto justify-center flex-wrap">
                            {readyPop.images.map((image, index) => (
                                <img
                                    key={index}
                                    alt={`준비완료 ${index + 1}`}
                                    src={`${api_uri}/${image}`}
                                    className="max-w-[70vw] max-h-[60vh] object-contain rounded-xl"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchTable;
